import type { UseMutationResult } from "@tanstack/react-query";
import type { RefObject } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getTooltipClassName } from "../../../../utils";

interface EmptyStateInputProps {
  isDarkMode: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  inputRef: RefObject<HTMLInputElement>;
  uploadAttachmentMutation: UseMutationResult<
    unknown,
    unknown,
    { chat_session_uid: string; file: File },
    unknown
  >;
  isLoading: boolean;
  onSendMessage: () => void;
  onUploadFile: (file: File) => void;
}

export function EmptyStateInput({
  isDarkMode,
  fileInputRef,
  inputRef,
  uploadAttachmentMutation,
  isLoading,
  onSendMessage,
  onUploadFile,
}: EmptyStateInputProps) {
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if we're already uploading or creating a session
    if (uploadAttachmentMutation.isLoading || isLoading) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    onUploadFile(file);
  };

  return (
    <div className="grid place-items-center place-content-center w-full h-full p-4 md:p-8">
      {/* Welcome Message */}
      <div className="mb-4 text-center max-w-2xl">
        <h2
          className={[
            "text-2xl",
            "md:text-3xl",
            "font-semibold",
            "mb-2",
            isDarkMode ? "text-gray-100!" : "text-gray-900",
          ].join(" ")}
        >
          Start a New Quotation Request Conversation
        </h2>
        <p
          className={[
            "text-base",
            "md:text-lg",
            isDarkMode ? "text-gray-400" : "text-gray-600",
          ].join(" ")}
        >
          Describe your travel needs and I'll create a quotation request for you.
        </p>
      </div>

      {/* Input Section */}
      <div
        className={[
          "w-full",
          "max-w-3xl",
          "grid",
          "grid-cols-[1fr_auto]",
          "gap-2",
          "md:gap-3",
          "items-center",
        ].join(" ")}
      >
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center min-w-0">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.jpg,.jpeg,.png,.gif,.webp"
            disabled={uploadAttachmentMutation.isLoading || isLoading}
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={handleFileClick}
            disabled={uploadAttachmentMutation.isLoading || isLoading}
            className={[
              "w-8",
              "h-8",
              "md:w-10",
              "md:h-10",
              "rounded-lg!",
              "grid",
              "place-items-center",
              "cursor-pointer",
              "transition-opacity",
              "upload-attachment-btn-empty",
              isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200",
              (uploadAttachmentMutation.isLoading || isLoading) && "opacity-50 cursor-not-allowed",
            ].join(" ")}
            aria-label="Upload file"
          >
            <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
              {uploadAttachmentMutation.isLoading ? "⏳" : "📎"}
            </span>
          </button>
          <ReactTooltip
            anchorSelect=".upload-attachment-btn-empty"
            place="top"
            content="Upload attachment"
            className={getTooltipClassName(isDarkMode)}
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your message to start a conversation..."
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSendMessage();
              }
            }}
            className={[
              "w-full",
              "min-w-0",
              "px-2",
              "md:px-4",
              "py-2",
              "md:py-3",
              "border",
              "rounded-lg",
              "focus:outline-none",
              "focus:ring-2",
              isDarkMode
                ? [
                    "border-gray-600",
                    "bg-gray-700",
                    "text-gray-100!",
                    "placeholder-gray-400",
                    "focus:ring-blue-400",
                  ].join(" ")
                : [
                    "border-gray-300",
                    "bg-white",
                    "text-gray-900",
                    "placeholder-gray-500",
                    "focus:ring-blue-500",
                  ].join(" "),
            ].join(" ")}
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isLoading) {
              onSendMessage();
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isLoading) {
              onSendMessage();
            }
          }}
          disabled={isLoading}
          className={[
            "w-10",
            "h-10",
            "md:w-12",
            "md:h-12",
            "rounded-lg!",
            "grid",
            "place-items-center",
            "select-none",
            "relative",
            "z-10",
            isDarkMode
              ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
              : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700",
            isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          ].join(" ")}
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        >
          <span className="text-white text-xl">→</span>
        </button>
      </div>
    </div>
  );
}
